import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth-store'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import type { User } from '@/types/auth'
import { Loader2, Brain, ShieldCheck, AlertCircle } from 'lucide-react'
import { BackgroundWrapper } from '@/components/ui/background-wrapper'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

// Secure user data validation schema for OAuth response
const oauthUserDataSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(200),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  avatarUrl: z.string().url().nullable(),
})

function AuthSuccessPage() {
  const navigate = useNavigate()
  const { access_token, refresh_token, user: userParam } = Route.useSearch()
  const [isProcessing, setIsProcessing] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const processTokens = async () => {
      try {
        // Validate required parameters
        if (!access_token || !refresh_token || !userParam) {
          throw new Error('Missing required authentication parameters')
        }

        // Check if tokens have already been processed to prevent replay attacks
        const processedTokens = sessionStorage.getItem('oauth_tokens_processed')
        if (processedTokens === access_token) {
          console.log('Tokens already processed, redirecting to dashboard')
          navigate({ to: '/dashboard', replace: true })
          return
        }

        // Safely parse and validate user data from URL parameter
        let oauthUserData
        try {
          const rawUserData =
            typeof userParam === 'string'
              ? JSON.parse(decodeURIComponent(userParam))
              : userParam

          // Validate user data against schema to prevent injection attacks
          oauthUserData = oauthUserDataSchema.parse(rawUserData)
          console.log('Validated OAuth user data:', oauthUserData)
        } catch (validationError) {
          console.error('User data validation failed:', validationError)
          throw new Error(
            'Invalid user data received from authentication server'
          )
        }

        // Map OAuth user data to full User interface expected by auth store
        const userData: User = {
          id: oauthUserData.id,
          email: oauthUserData.email,
          username: oauthUserData.email.split('@')[0] || 'user', // Extract username from email
          firstName: oauthUserData.firstName,
          lastName: oauthUserData.lastName,
          fullName: oauthUserData.name,
          bio: null,
          website: null,
          avatarUrl: oauthUserData.avatarUrl,
          emailVerified: true, // OAuth users are considered email verified
          isActive: true,
          lastLoginAt: new Date().toISOString(),
          loginCount: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          metadata: {},
        }

        // Validate token format (basic JWT structure check)
        const validateJWTFormat = (token: string): boolean => {
          const parts = token.split('.')
          return parts.length === 3 && parts.every(part => part.length > 0)
        }

        if (
          !validateJWTFormat(access_token) ||
          !validateJWTFormat(refresh_token)
        ) {
          throw new Error('Invalid token format received')
        }

        // Store tokens in localStorage (TODO: Move to httpOnly cookies for production)
        localStorage.setItem('access_token', access_token)
        localStorage.setItem('refresh_token', refresh_token)

        // Update auth store with user data
        useAuthStore.setState({
          user: userData,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          validationErrors: null,
        })

        // Mark tokens as processed to prevent reuse
        sessionStorage.setItem('oauth_tokens_processed', access_token)

        // Clean up OAuth state
        sessionStorage.removeItem('oauth_state')

        // Wait a moment to show success state
        await new Promise(resolve => setTimeout(resolve, 1500))

        // Clear URL parameters and redirect to dashboard using replace to prevent back button exploit
        window.history.replaceState({}, document.title, '/auth/success')
        navigate({ to: '/dashboard', replace: true })
      } catch (error) {
        console.error('Error processing OAuth success:', error)
        setError(
          error instanceof Error
            ? error.message
            : 'Failed to complete authentication'
        )
        setIsProcessing(false)
      }
    }

    processTokens()
  }, [access_token, refresh_token, userParam, navigate])

  if (error) {
    return (
      <BackgroundWrapper>
        <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md">
            <Card className="bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                      <Brain className="h-5 w-5 text-black" />
                    </div>
                    <span className="text-lg font-semibold">ThoughtFabric</span>
                  </div>

                  <div className="space-y-3">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
                    <h1 className="text-xl font-semibold">
                      Authentication failed
                    </h1>

                    <div className="bg-destructive/15 border border-destructive/20 text-destructive text-xs px-3 py-2 rounded-md">
                      {error}
                    </div>
                  </div>

                  <div className="space-y-2 w-full">
                    <Button
                      onClick={() => navigate({ to: '/auth/login' })}
                      className="w-full bg-black hover:bg-black/90 text-white"
                    >
                      Try again
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="text-center text-xs text-balance text-gray-400 mt-6">
              By continuing, you agree to our{' '}
              <a
                href="https://thoughtfabric.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-white transition-colors"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://thoughtfabric.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              .
            </div>
          </div>
        </div>
      </BackgroundWrapper>
    )
  }

  if (isProcessing) {
    return (
      <BackgroundWrapper>
        <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md">
            <Card className="bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                      <Brain className="h-5 w-5 text-black" />
                    </div>
                    <span className="text-lg font-semibold">ThoughtFabric</span>
                  </div>
                  <div className="space-y-3">
                    <ShieldCheck className="h-12 w-12 text-green-600 mx-auto" />
                    <h1 className="text-xl font-semibold">
                      Sign in successful!
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Welcome! Redirecting you to your dashboard...
                    </p>
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="text-center text-xs text-balance text-gray-400 mt-6">
              By continuing, you agree to our{' '}
              <a
                href="https://thoughtfabric.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-white transition-colors"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://thoughtfabric.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              .
            </div>
          </div>
        </div>
      </BackgroundWrapper>
    )
  }

  return null
}

export const Route = createFileRoute('/auth/success/')({
  validateSearch: z.object({
    access_token: z.string().optional(),
    refresh_token: z.string().optional(),
    user: z
      .union([
        z.string(),
        z.object({
          id: z.string(),
          email: z.string(),
          name: z.string(),
          firstName: z.string(),
          lastName: z.string(),
          avatarUrl: z.string().nullable(),
        }),
      ])
      .optional(),
  }),
  beforeLoad: async ({ search }) => {
    // If user is already authenticated and no new tokens, redirect to dashboard
    const { useAuthStore } = await import('@/stores/auth-store')
    const { isAuthenticated } = useAuthStore.getState()

    if (isAuthenticated && !search.access_token) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: AuthSuccessPage,
})
